/** @format */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, generate, Observable, Subject, takeUntil } from 'rxjs';
import { Profile } from 'src/app/models/profile';
import { TimeBank, BankType } from 'src/app/models/timeBank';
import {
	getApprovedTimesheets,
	getExceptionTypes,
	getPayPeriods,
	getProfile,
	getTeamProfile,
	getTimeBanks,
	getBankTypes,
	getAllUserTimesheets,
	getTimeEntries,
	getSystemUser,
} from 'src/app/store';
import {
	ApplicationState,
	CalendarMonthlyEntry,
	ExceptionType,
	GetTeamProfilePicsBulk,
	ManagerEmployeeTimesheetView,
	PayPeriod,
	TeamMemberProfile,
	TimeentryDB,
	UserTimesheet,
	SubmitTimeEntries,
	TimeentryDBByPeriod,
	GetApprovedTimesheets,
	Timesheet,
	GetTimeEntries,
	UserNotification,
	NotificationStates,
	ToastUserNotification,
	User,
	CloseTimeEntries,
	PayPeriodCdoUsageTracker,
} from 'src/app/store/application';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';
import { formatDate } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { roles } from 'src/app/app.module';

@Component({
	selector: 'app-postings',
	templateUrl: './postings.component.html',
	styleUrls: ['./postings.component.scss'],
	standalone: false,
})
export class PostingsComponent implements OnInit, OnDestroy {
	private ngUnsubscribe: Subject<void> = new Subject();

	calendarEntries$: Observable<CalendarMonthlyEntry[]> | undefined;
	calendarEntries: CalendarMonthlyEntry[] = [];

	employeeTimesheets$: Observable<ManagerEmployeeTimesheetView[]> | undefined;
	employeeTimesheets: ManagerEmployeeTimesheetView[] = [];

	teamProfile$: Observable<TeamMemberProfile[]> | undefined;
	teamProfile: TeamMemberProfile[] = [];

	profile$: Observable<Profile>;
	profile!: Profile;

	payPeriods$: Observable<PayPeriod[]>;
	payPeriods!: PayPeriod[];

	bankTypes$: Observable<BankType[]>;
	bankTypes: BankType[] = [];
	timeBanks$: Observable<TimeBank[]>;
	timeBanks: TimeBank[] = [];

	exceptionTypes$!: Observable<ExceptionType[]>;
	exceptionTypes!: ExceptionType[];

	allUserTimesheets$!: Observable<UserTimesheet[]>;
	allUserTimesheets: UserTimesheet[] = [];

	approvedTimesheets$!: Observable<UserTimesheet[]>;
	approvedTimesheets: UserTimesheet[] = [];

	timeEntries$!: Observable<TimeentryDB[]>;
	timeEntries!: TimeentryDB[];

	systemUser$!: Observable<User>;
	systemUser!: User;

	aptsTest: UserTimesheet[] = [];

	usersWithExceptions: UserTimesheet[] = [];
	usersWithExceptionsCopy: UserTimesheet[] = [];

	monthName: string = '';

	addRegularHoursChecked = false;
	addMonthPostingsChecked = false;
	showSystemSubmissionsChecked = false;

	quickSelect: string = 'all';
	currentWeek = ``;
	payperiodWeek: string | undefined = '';
	timeEntriesFromTimesheets: TimeentryDB[] = [];
	timeEntriesFromTimesheetsCopy: TimeentryDB[] = [];

	timeEntriesDBByPeriod: TimeentryDBByPeriod[] = [];
	tkForm: UntypedFormGroup;
	emailsMap: { [id: string]: string } = {};
	emailDateMap: { [id: string]: { email: string; week: string; startDate: Date } } = {};
	Object = Object;

	@ViewChild('batchModal') batchModal: any;
	batchNumberForm: UntypedFormGroup;
	batchModalRef!: NgbModalRef;
	newCount = 0;

	currentLoggedInUser: AccountInfo | null = null;
	isManagerView: boolean = false;
	isManagerAndTimekeeperView: boolean = false;

	constructor(
		private store: Store<ApplicationState>,
		private formBuilder: UntypedFormBuilder,
		public router: Router,
		private clipboard: Clipboard,
		private modalService: NgbModal,
		private authService: MsalService,
	) {
		// this.employeeTimesheets$ = this.store.select(getEmployeeTimesheets);
		// this.timesheets$ = this.store.select(getTimesheets);
		this.timeEntries$ = this.store.select(getTimeEntries);
		this.allUserTimesheets$ = this.store.select(getAllUserTimesheets);
		this.teamProfile$ = this.store.select(getTeamProfile);
		this.profile$ = this.store.select(getProfile);
		this.exceptionTypes$ = this.store.select(getExceptionTypes);
		this.payPeriods$ = this.store.select(getPayPeriods);
		this.approvedTimesheets$ = this.store.select(getApprovedTimesheets);
		this.systemUser$ = this.store.select(getSystemUser);

		this.bankTypes$ = this.store.select(getBankTypes);
		this.timeBanks$ = this.store.select(getTimeBanks);

		this.tkForm = this.formBuilder.group({
			apprEmps: this.formBuilder.array([]),
		});

		this.batchNumberForm = this.formBuilder.group({
			batchNum: this.formBuilder.control('', Validators.required),
		});

		this.monthName = HelperFunctions.getMonthName(new Date().getFullYear(), new Date().getMonth());
		this.currentLoggedInUser = this.authService.instance.getActiveAccount()
			? this.authService.instance.getActiveAccount()
			: this.authService.instance.getAllAccounts()[0];

		if (this.currentLoggedInUser) {
			let userRoles = this.currentLoggedInUser.idTokenClaims?.roles || [];

			if (userRoles.some((role) => role === roles.Manager)) this.isManagerView = true;
			if (userRoles.some((role) => role === roles.TimeKeepers)) this.isManagerView = false;
			if (userRoles.some((role) => role === roles.Manager) && userRoles.some((role) => role === roles.TimeKeepers)) {
				this.isManagerAndTimekeeperView = true;
			}

			// NOTE: DEV OVERRIDE FOR TESTING MANAGER VIEW
			// this.isManagerView = true;
		}
	}

	ngOnInit(): void {
		// this.store.dispatch(GetEmployeeTimesheets());
		this.store.dispatch(GetApprovedTimesheets({ allMonthTimesheets: this.addMonthPostingsChecked, isManagerView: this.isManagerView }));
		this.store.dispatch(GetTimeEntries());

		let upns: string[] = [];
		this.newCount = 0;

		combineLatest([
			this.approvedTimesheets$,
			this.allUserTimesheets$,
			this.exceptionTypes$,
			this.timeEntries$,
			this.payPeriods$,
			this.systemUser$,
			this.profile$,
		]).subscribe(([apts, ats, ex, timeEntriesWithBatchNum, pp, sysUser, currentUser]) => {
			this.payPeriods = pp;
			this.setCurrentPayPeriodText();

			if (apts.length === 0 || ats.length === 0 || ex.length === 0) {
				return;
			}

			// this.setPayPeriodTitleText(apts);
			this.profile = currentUser;

			// filter it based on the timekeeper responsible.
			let approvedFilteredTimesheets = this.getRelatedPostings(apts);
			let allFilteredTimesheets = this.getRelatedPostings(ats);

			this.systemUser = sysUser;
			this.aptsTest = approvedFilteredTimesheets;
			this.allUserTimesheets = allFilteredTimesheets;
			this.exceptionTypes = ex;
			this.timeEntries = timeEntriesWithBatchNum;
			this.timeEntriesFromTimesheets = [];
			this.timeEntriesDBByPeriod = [];
			this.usersWithExceptions = [];

			approvedFilteredTimesheets.forEach((uts) => {
				if (!upns.includes(uts.employeeEmail)) {
					upns = [...upns, uts.employeeEmail];
				}

				if (!!allFilteredTimesheets && !!ex && !!timeEntriesWithBatchNum && !!pp) {
					this.buildUserTimeEntries(uts);
				}
			});

			if (upns.length > 0) {
				this.store.dispatch(GetTeamProfilePicsBulk({ upns }));
			}

			this.approvedTimesheets = [...approvedFilteredTimesheets].sort((a, b) => a.employeeFirstName.localeCompare(b.employeeFirstName));

			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.sort((a, b) => {
				return this.userName(a.userId || 0).localeCompare(this.userName(b.userId || 0)) || a.entryDate.getTime() - b.entryDate.getTime();
			});

			this.approvedTimesheets.forEach(() => {
				const fg = this.formBuilder.group({
					chkbox: true,
				});
				this.appArr.push(fg);
			});

			this.timeEntriesFromTimesheets.forEach((te) => {
				let userId = te.userId;
				if (!this.usersWithExceptions.find((user) => user.userId === userId) && this.approvedTimesheets.length > 0) {
					let userRecord = this.approvedTimesheets.find((ts) => ts.userId === userId)!;
					this.usersWithExceptions.push(userRecord);
				}
			});

			this.newCount = this.timeEntriesFromTimesheets.filter((t) => t.isNew === true).length;
			this.timeEntriesFromTimesheetsCopy = JSON.parse(JSON.stringify(this.timeEntriesFromTimesheets));
			this.toggleAll(true);

			if (this.addRegularHoursChecked) {
				this.onChkboxAddRegularHours(false);
			}
		});

		// this.payPeriods$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(payPeriods => {
		//   this.payPeriods = payPeriods;
		// });

		this.teamProfile$?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((team) => {
			this.teamProfile = team;
		});

		this.bankTypes$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((bt) => {
			this.bankTypes = bt;
		});
		this.timeBanks$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((tb) => {
			this.timeBanks = tb;
		});
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.unsubscribe();
	}

	getRelatedPostings(timesheets: UserTimesheet[]) {
		let filteredTimesheets: UserTimesheet[] = [];
		if (this.profile?.user) {
			let managedUsers = [...this.profile.manage, ...this.profile.delegated, ...this.profile.managedGoneFromDNV];
			let managedUserIds = managedUsers.map((user) => user.user.id);

			let timekeeperManagedUsers = [...this.profile.timekeeperDelegated, ...this.profile.timekeeperManagedUsers];
			let timekeeperManagedUserIds = timekeeperManagedUsers.map((user) => user.user.id);

			if (this.isManagerView && !this.isManagerAndTimekeeperView) {
				// user is a manager
				filteredTimesheets = timesheets.filter((ts) => managedUserIds.includes(ts.userId));
			} else if (!this.isManagerView && !this.isManagerAndTimekeeperView) {
				// user is a timekeeper
				filteredTimesheets = timesheets.filter((ts) => timekeeperManagedUserIds.includes(ts.userId));
			} else {
				if (managedUsers.length > 0 && timekeeperManagedUsers.length === 0) {
					// user is a manager primarily
					this.isManagerView = true;
					filteredTimesheets = timesheets.filter((ts) => managedUserIds.includes(ts.userId));
				} else {
					// user is a both but timekeeper takes precedence
					this.isManagerView = false;
					filteredTimesheets = timesheets.filter((ts) => timekeeperManagedUserIds.includes(ts.userId));
				}
			}
		}

		return filteredTimesheets;
	}

	findUserTimeBank(userId: number) {
		let managedUsers = [
			...this.profile.manage,
			...this.profile.delegated,
			...this.profile.managedGoneFromDNV,
			...this.profile.timekeeperDelegated,
			...this.profile.timekeeperManagedUsers,
		];

		let managedTimebanks = managedUsers.map((user) => ({
			id: user.user.id,
			timeBank: user.timeBanks,
		}));

		return managedTimebanks.find((tb) => tb.id === userId)?.timeBank || [];
	}

	buildUserTimeEntries(uts: UserTimesheet) {
		let usedBank: { [id: string]: number } = {};
		let timeEntries: TimeentryDB[] = [];
		let rbEntries: Timesheet[] = [];
		let rolledbackEntries: TimeentryDB[] = [];
		let payPeriodTsCdoTracker: PayPeriodCdoUsageTracker = {};
		let rbPayPeriodTsCdoTracker: PayPeriodCdoUsageTracker = {};

		uts.timesheets.forEach((ts) => {
			let siblingTimesheet = uts.timesheets.find((ts_user) => ts_user.payPeriod === ts.payPeriod && ts_user.id !== ts.id);
			let userTimeBanks = this.findUserTimeBank(uts.userId);
			let prevOvertimeTimeBank = this.bankTypes.find((bt) => bt.name === 'Overtime - Prior Year')!;
			let prevYearOTBank = userTimeBanks.find((utb) => utb.bankTypeId === prevOvertimeTimeBank?.id);

			const generatedTimeEntries = HelperFunctions.createTimeEntries(
				ts,
				uts.configurations,
				this.exceptionTypes,
				this.timeEntriesDBByPeriod,
				usedBank,
				this.timeEntries,
				{
					cdoUsageTracker: payPeriodTsCdoTracker,
					ignoreStatus: Number(ts.approvedBy) === this.systemUser.id && ts.status === 'Deleted',
					siblingTimesheetEntry: siblingTimesheet,
					prevYearOTBank,
				},
			);

			const prepTimeEntries = generatedTimeEntries.prepTimeEntry;
			this.timeEntriesDBByPeriod = generatedTimeEntries.timeEntriesByPeriod;

			var user = this.allUserTimesheets.find((rbUser) => rbUser.userId === uts.userId);
			if (user !== undefined && user?.timesheetsRolledBack !== null) {
				//Find latest rolledback timesheet based on start of week.
				rbEntries = user
					?.timesheetsRolledBack!.filter((rbts) => rbts.startDate.getTime() === ts.startDate.getTime())
					.sort((a, b) => {
						return b.rolledBackDateTime!.getTime() - a.rolledBackDateTime!.getTime();
					});
			}

			if (rbEntries.length > 0) {
				let generatedTimeEntries = HelperFunctions.createTimeEntries(
					rbEntries[0],
					uts.configurations,
					this.exceptionTypes,
					this.timeEntriesDBByPeriod,
					usedBank,
					this.timeEntries,
					{
						cdoUsageTracker: rbPayPeriodTsCdoTracker,
						ignoreStatus: true,
						outputRegularEntries: false,
						rollingBackTimesheet: ts,
						prevYearOTBank,
					},
				);
				rolledbackEntries = generatedTimeEntries.prepTimeEntry;
				this.timeEntriesDBByPeriod = generatedTimeEntries.timeEntriesByPeriod;

				if (rolledbackEntries.length > 0) {
					var correctedte = HelperFunctions.correctionTimeEntries(
						prepTimeEntries,
						ts,
						rolledbackEntries,
						this.exceptionTypes,
						uts.configurations,
						ts.startDate,
						this.payPeriods || [],
					);

					// populate batch numbers
					correctedte.forEach((entry) => {
						const batchNumber = this.timeEntries.find((entry) => entry.timesheetId === ts.id)?.batchNumber;
						entry.batchNumber = batchNumber;
						entry.isNew = batchNumber === undefined ? true : false;
					});

					// Correct entries if there is a rollback
					this.timeEntriesFromTimesheets.push(...correctedte);
				} else {
					timeEntries.push(...prepTimeEntries);
					usedBank = HelperFunctions.compileUsedEntries(timeEntries, this.bankTypes);
					this.timeEntriesFromTimesheets.push(...prepTimeEntries);
				}
			} else {
				timeEntries.push(...prepTimeEntries);
				usedBank = HelperFunctions.compileUsedEntries(timeEntries, this.bankTypes);
				this.timeEntriesFromTimesheets.push(...prepTimeEntries); // TODO Fix me
			}
		});
	}

	onChkboxAddRegularHours(needsUsersListReevaluation: boolean) {
		let timeEntries: TimeentryDB[] = [];
		let usedBank: { [id: string]: number } = {};

		if (this.addRegularHoursChecked) {
			if (needsUsersListReevaluation) {
				this.usersWithExceptionsCopy = JSON.parse(JSON.stringify(this.usersWithExceptions));
			}

			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.filter((t) => t.exceptionTypeId !== 1 && t.exceptionTypeId !== 2);

			this.aptsTest.forEach((uts) => {
				const chkboxIndex = this.usersWithExceptions.findIndex((u) => u.userId === uts.userId);

				//-1 chkboxIndex means user has approved timesheets but no exceptions. We want to add the regular hours for that user too.
				if ((chkboxIndex >= 0 && this.appArr.at(chkboxIndex).get('chkbox')?.value === true) || chkboxIndex === -1) {
					uts.timesheets.forEach((ts) => {
						const generatedTimeEntries = HelperFunctions.addRegularHoursEntries(
							ts,
							uts.configurations,
							this.exceptionTypes,
							this.timeEntriesDBByPeriod,
						);
						timeEntries.push(...generatedTimeEntries.prepTimeEntry);
						usedBank = HelperFunctions.compileUsedEntries(timeEntries, this.bankTypes);
						this.timeEntriesFromTimesheets.push(...generatedTimeEntries.prepTimeEntry); // TODO Fix me
					});
				}
			});
		} else {
			// TODO: Need to fix issue when going from add regular hours to original list not rendering correctly
			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.filter((t) => t.exceptionTypeId !== 1 && t.exceptionTypeId !== 2);
			this.usersWithExceptions = JSON.parse(JSON.stringify(this.usersWithExceptionsCopy));
		}

		this.timeEntriesFromTimesheets.sort((a, b) => {
			return this.userName(a.userId || 0).localeCompare(this.userName(b.userId || 0)) || a.entryDate.getTime() - b.entryDate.getTime();
		});

		if (needsUsersListReevaluation) {
			// this.usersWithExceptions = [];
			this.timeEntriesFromTimesheets.forEach((te) => {
				let userId = te.userId;
				if (!this.usersWithExceptions.find((user) => user.userId === userId) && this.approvedTimesheets.length > 0) {
					let userRecord = this.approvedTimesheets.find((ts) => ts.userId === userId)!;
					this.usersWithExceptions.push(userRecord);
				}
			});
		}
	}

	onChkboxOnlyShowSystemSubmissions() {
		if (this.showSystemSubmissionsChecked) {
			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.filter(
				(t) => t.exceptionTypeId !== 1 && t.exceptionTypeId !== 2 && this.isSystemSubmission(t.userId!, t.entryDate),
			);
		} else {
			this.timeEntriesFromTimesheets = JSON.parse(JSON.stringify(this.timeEntriesFromTimesheetsCopy));
		}

		this.timeEntriesFromTimesheets.sort((a, b) => {
			return this.userName(a.userId || 0).localeCompare(this.userName(b.userId || 0)) || a.entryDate.getTime() - b.entryDate.getTime();
		});
	}

	onChkboxAddMonthPostings() {
		this.store.dispatch(GetApprovedTimesheets({ allMonthTimesheets: this.addMonthPostingsChecked }));
	}

	onChkboxChange(evt: any, userId: number): void {
		this.quickSelect = '';
		if (evt.target?.checked) {
			this.timeEntriesDBByPeriod = [];

			this.approvedTimesheets
				.filter((uts) => uts.userId === userId)
				.forEach((uts) => {
					this.buildUserTimeEntries(uts);
				});

			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.sort((a, b) => {
				return this.userName(a.userId || 0).localeCompare(this.userName(b.userId || 0)) || a.entryDate.getTime() - b.entryDate.getTime();
			});

			let hasAllSelectedUsers = true;
			for (let control of this.appArr['controls']) {
				if (!control.value.chkbox) {
					hasAllSelectedUsers = false;
				}
			}

			if (hasAllSelectedUsers) {
				this.quickSelect = 'all';
			}

			if (this.addRegularHoursChecked) {
				this.onChkboxAddRegularHours(false);
			}
		} else {
			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.filter((uts) => uts.userId !== userId);
			this.quickSelect = '';
			// this.timeEntriesDBByPeriod = this.timeEntriesDBByPeriod.filter(uts => uts.timeEntries.find(ts => ts.userId !== userId)); // no need to remove these as all entries get copied and sent to JDE

			let hasSelectedUser = false;
			for (let control of this.appArr['controls']) {
				if (control.value.chkbox) {
					hasSelectedUser = true;
				}
			}

			if (!hasSelectedUser) {
				this.quickSelect = 'none';
			}
		}
	}

	toggleAll(val: boolean): void {
		this.timeEntriesFromTimesheets = [];

		if (val === false) {
			this.quickSelect = 'none';
		} else {
			this.quickSelect = 'all';
			this.timeEntriesDBByPeriod = [];

			this.approvedTimesheets.forEach((uts) => {
				this.buildUserTimeEntries(uts);
			});

			this.timeEntriesFromTimesheets = this.timeEntriesFromTimesheets.sort((a, b) => {
				return this.userName(a.userId || 0).localeCompare(this.userName(b.userId || 0)) || a.entryDate.getTime() - b.entryDate.getTime();
			});
		}

		for (let control of this.appArr['controls']) {
			control.setValue({ chkbox: val });
		}

		if (this.quickSelect === 'all' && this.addRegularHoursChecked) {
			this.onChkboxAddRegularHours(false);
		}
	}

	isSelected(i: number): boolean {
		if (this.appArr.at(i).get('chkbox')?.value === true) {
			return true;
		} else {
			return false;
		}
	}

	get appArr() {
		return this.tkForm.get('apprEmps') as UntypedFormArray;
	}

	getMemberProfile(email: string): SafeUrl | null {
		const member = this.teamProfile.filter((t) => HelperFunctions.compareEmail(t.email, email));
		if (member.length > 0) {
			return member[0].profilePic;
		}
		return null;
	}

	timeEntryToClipboardTemporary() {
		let text = ``;

		this.timeEntriesFromTimesheets.forEach((entry) => {
			const code = this.exceptionTypes.find((e) => e.id === entry.exceptionTypeId)?.code ?? 0;
			text += (
				`${this.userName(entry.userId ?? 0)}\t${this.userEmployeeNumber(entry.userId ?? 0)}\t${this.formatDate(entry.entryDate)}\t${code}\t${entry.duration}\t${entry.comments || ''}\t${entry.rate || ''}\t${entry.jobType || ''}\t${entry.jobStep || ''}\t${entry.workOrderNumber || ''}`
			).replaceAll("\n", "");

			text += "\n";
		});

		return text;
	}

	timeEntryToClipboard() {
		let text = ``;

		this.timeEntriesFromTimesheets.forEach((entry) => {
			const code = this.exceptionTypes.find((e) => e.id === entry.exceptionTypeId)?.code ?? 0;
			text += (
				`${this.userEmployeeNumber(entry.userId ?? 0)}\t${this.formatDate(entry.entryDate)}\t${code}\t
				${entry.duration}\t${entry.comments || ''}\t${entry.rate || ''}\t${entry.jobType || ''}\t
				${entry.jobStep || ''}\t${entry.workOrderNumber || ''}`
			).replaceAll("\n", "");
			
			text += '\n';
		});

		return text;
	}

	triggerCopyToClipboard(withName: boolean = false) {
		let text = withName ? this.timeEntryToClipboardTemporary() : this.timeEntryToClipboard();
		this.clipboard.copy(text);

		const clipBoardNotification: UserNotification = {
			type: NotificationStates.Success,
			title: 'Copied to clipboard',
			message: `Time entries have been copied to clipboard`,
		};

		this.store.dispatch(ToastUserNotification({ notification: clipBoardNotification }));
	}

	openBatchModal(withName: boolean = false) {
		this.triggerCopyToClipboard(withName);

		const ngbModalOptions: NgbModalOptions = {
			backdrop: 'static',
			keyboard: false,
			centered: true,
		};

		this.batchModalRef = this.modalService.open(this.batchModal, ngbModalOptions);
		this.batchModalRef.result.then(
			() => {
				const approvedTimesheetsIds = this.approvedTimesheets.flatMap((apts) => apts.timesheets.map((ts) => ts.id));
				let timeEntries = this.timeEntriesFromTimesheets.map((entry) => ({
					...entry,
					comments: entry.comments || '',
					batchNumber: this.batchNumberForm.value.batchNum,
				}));

				//Remove regular hours before sending to server side. We don't store regular hours as exception entries.
				if (this.addRegularHoursChecked) {
					timeEntries = timeEntries.filter((t) => t.exceptionTypeId !== 1 && t.exceptionTypeId !== 2);
				}

				this.store.dispatch(
					SubmitTimeEntries({
						approvedTimesheetsIds,
						timeEntries,
						allMonthTimesheets: this.addMonthPostingsChecked,
						batchNumber: Number(this.batchNumberForm.value.batchNum),
						isManagerView: this.isManagerView,
					}),
				);
			},
			() => {},
		);
	}

	timekeeperCloseTimeEntry() {
		this.store.dispatch(CloseTimeEntries({ allMonthTimesheets: this.addMonthPostingsChecked, isManagerView: this.isManagerView }));
	}

	// setPayPeriodTitleText(approvedTimesheets: UserTimesheet[]) {
	// 	const allTimesheets = approvedTimesheets.map((apt) => apt.timesheets).flat();
	// 	const latestTimesheet = allTimesheets.reduce((a, b) => (a.endDate > b.endDate ? a : b));
	// 	this.payperiodWeek = '';

	// 	if (!!this.payPeriods) {
	// 		const endOfCurrentPP = this.payPeriods.find((pp) => pp.payPeriod == latestTimesheet.payPeriod)?.endDate;
	// 		this.currentWeek = `Time Entries up to ${formatDate(endOfCurrentPP ?? 0, 'MMMM dd, yyyy', 'en-CA')}`;
	// 		const currentDate = new Date();
	// 		const wk2Start = HelperFunctions.getFutureDate(latestTimesheet.payPeriodStartDate, 7);

	// 		this.payperiodWeek = 'Week ' + (currentDate < wk2Start ? '1' : '2') + ' of Pay Period ' + Number(latestTimesheet.payPeriod.substring(4));
	// 	}
	// }

	setCurrentPayPeriodText() {
		this.payperiodWeek = '';
		let currentDate = HelperFunctions.getBeginningOfDayDate(new Date());
		let currentPayPeriod = this.payPeriods.filter(
			(p) => (p.startDate <= currentDate && currentDate <= p.endDate) || (p.endDate <= currentDate && currentDate <= p.timeEntryCutOffDate),
		)[0];

		let wk2Start = HelperFunctions.getFutureDate(currentPayPeriod.startDate, 7);
		this.payperiodWeek = 'Week ' + (currentDate < wk2Start ? '1' : '2') + ' of Pay Period ' + Number(currentPayPeriod.payPeriod.substring(4));
	}

	selectAll() {
		this.appArr.controls.forEach((fc) => {
			fc.get('chkbox')?.setValue(true);
		});
	}

	selectNone() {
		this.appArr.controls.forEach((fc) => {
			fc.get('chkbox')?.setValue(false);
		});
	}

	dismissModal() {
		this.batchModalRef.dismiss();
	}

	closeModal() {
		this.batchModalRef.close();
	}

	formatDate(date: Date) {
		return formatDate(date, 'yyyy/MM/dd', 'en-CA');
		// return formatDate(date, 'MM-dd-yyyy', 'en-CA');
	}

	exceptionCode(id: number) {
		return this.exceptionTypes.find((ex) => ex.id == id)?.code;
	}

	exceptionDescription(id: number) {
		return this.exceptionTypes.find((ex) => ex.id == id)?.description;
	}

	userName(userId: number) {
		return (
			this.approvedTimesheets.find((uts) => uts.userId === userId)?.employeeFirstName +
			' ' +
			this.approvedTimesheets.find((uts) => uts.userId === userId)?.employeeLastName
		);
	}

	userEmployeeNumber(userId: number) {
		return this.approvedTimesheets.find((uts) => uts.userId === userId)?.employeeNumber;
	}

	isSystemSubmission(userId: number, entryDate: Date) {
		let userTimesheets = this.allUserTimesheets.find((ats) => ats.userId === userId)!;

		if (userTimesheets) {
			let timesheet = userTimesheets.timesheets.find((ts) => HelperFunctions.getDateIsWithinBounds(ts.startDate, ts.endDate, entryDate));
			return Number(timesheet?.approvedBy) === this.systemUser.id;
		}

		return false;
	}

	getEntrySubmitter(entry: TimeentryDB) {
		return Number(entry.approverId) === this.systemUser.id ? 'System' : 'Manager';
	}
}
